/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import ExportedImage from 'next-image-export-optimizer'
import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'

const LayananFAQ = dynamic(() => import('@/components/organisms/layanan/layanan-faq').then((mod) => mod.LayananFAQ))
const LayananHero = dynamic(() => import('@/components/organisms/layanan/layanan-hero').then((mod) => mod.LayananHero))
const LayananStep = dynamic(() => import('@/components/organisms/layanan/layanan-step').then((mod) => mod.LayananStep))
const LayananTestimonial = dynamic(() =>
  import('@/components/organisms/layanan/layanan-testimonial').then((mod) => mod.LayananTestimonial)
)
const LayananList = dynamic(() => import('@/components/organisms/layanan/layanan-list').then((mod) => mod.LayananList), {
  loading: () => (
    <div className="flex items-center justify-center w-full h-[40vh]">
      <Loading className="w-9 h-9 text-primary-blue" />
    </div>
  )
})
import { Loading } from '@/components/atoms/icons'
import Container from '@/components/templates/container/container'
import { SiteLayout } from '@/components/templates/site-layout'

import { formatString } from '@/lib/utils/format-string'
import { getAssetData } from '@/lib/utils/get-asset-data'

interface Props {
  data: VO
  categoryList?: string[]
}

export const VirtualOffice = ({ data, categoryList }: Props) => {
  const tabList = useMemo(() => {
    if (!data?.items) return []

    const serviceTabIds = data.items.flatMap((item) => item.fields.serviceTab?.map((tab) => tab.sys.id) ?? [])
    const idToIndex = new Map(serviceTabIds.map((id, index) => [id, index]))

    return data.includes.Entry.filter((entry) => idToIndex.has(entry.sys.id)).sort(
      (a, b) => (idToIndex.get(a.sys.id) ?? -1) - (idToIndex.get(b.sys.id) ?? -1)
    )
  }, [data])

  const [activeTab, setActiveTab] = useState(formatString(tabList[0].fields.title))

  const renderOptions: Options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node, _) => {
        const image = getAssetData(node.data.target.sys.id, data)?.fields

        return (
          <ExportedImage
            src={image?.file.url ? 'https:' + image?.file.url : '/static/images/virtual-office.png'}
            height={image?.file.details.image.height || 456}
            width={image?.file.details.image.width || 904}
            alt={image?.title ? image.title : 'Image of virtual office'}
            className="object-contain w-full py-2 lg:py-4"
          />
        )
      }
    }
  }

  return (
    <SiteLayout serviceAble>
      <LayananHero title={data?.items[0]?.fields?.title} description={data?.items[0]?.fields?.description} />
      <LayananList
        data={data as any}
        tabList={tabList as any}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        categoryList={categoryList as string[]}
      />

      {data?.items[0]?.fields?.about && (
        <section className="bg-slate-50 py-9 lg:py-16">
          <Container className="max-w-[872px] flex flex-col gap-4 lg:gap-6 lg:text-center">
            <h2 className="text-2xl font-extrabold text-primary-blue lg:text-4xl">Apa Itu Virtual Office?</h2>
            <article className="container-markdown">
              {documentToReactComponents(data?.items[0]?.fields?.about, renderOptions)}
            </article>
          </Container>
        </section>
      )}

      <LayananStep
        title="Bagaimana Proses Sewa Virtual Office?"
        description="Anda bisa mendapatkan <i>virtual office</i> dari Easybiz dalam 4 langkah berikut."
        data={data.items[0]?.fields?.steps}
      />

      {data?.items[0]?.fields?.testimonialShow === true && (
        <LayananTestimonial data={data as any} testimonialVideo={data?.items[0]?.fields?.testimonialVideo ?? false} />
      )}

      <LayananFAQ data={data as any} activeTab={tabList[0].fields.title ?? 'Virtual Office Tab'} />
    </SiteLayout>
  )
}
